import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiErrorDetail } from "../api";
import { AuthContext } from "../AuthContext";

const inputClass =
  "appearance-none rounded-lg relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500";

const Logo = () => (
  <div className="flex justify-center mb-4">
    <div className="flex items-center">
      <div className="flex flex-col space-y-1 mr-3">
        <div className="w-8 h-2 bg-slate-700 rounded-sm"></div>
        <div className="w-8 h-2 bg-slate-700 rounded-sm"></div>
        <div className="w-8 h-2 bg-teal-400 rounded-sm"></div>
      </div>
      <h1 className="text-4xl font-bold text-gray-900">Accountz</h1>
    </div>
  </div>
);

const AuthPage = () => {
  // mode: "login" | "register" | "forgot" | "reset"
  const [mode, setMode] = useState("login");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: "",
    resetToken: "",
    newPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, register } = React.useContext(AuthContext);

  // Detect reset token in the URL (?reset_token=...) and switch to reset mode
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("reset_token");
    if (token) {
      setMode("reset");
      setFormData((prev) => ({ ...prev, resetToken: token }));
    }
  }, []);

  const switchMode = (next) => {
    setError("");
    setMode(next);
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const result =
      mode === "login"
        ? await login(formData.email, formData.password)
        : await register(formData.email, formData.password, formData.fullName);

    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: formData.email,
      });
      toast.success(response.data.message);
      switchMode("login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token: formData.resetToken,
        new_password: formData.newPassword,
      });
      toast.success(response.data.message);
      // Clean the token from the URL and return to login
      window.history.replaceState({}, document.title, window.location.pathname);
      setFormData((prev) => ({ ...prev, resetToken: "", newPassword: "", password: "" }));
      switchMode("login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset password.");
    } finally {
      setLoading(false);
    }
  };

  const heading = {
    login: "Sign in to your account",
    register: "Create your account",
    forgot: "Reset your password",
    reset: "Choose a new password",
  }[mode];

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" data-testid="auth-page">
      <div className="max-w-md w-full space-y-8">
        <div>
          <Logo />
          <h2 className="text-center text-2xl font-semibold text-gray-700">{heading}</h2>
          <p className="mt-2 text-center text-sm text-gray-600">All your accounts. One secure place.</p>
        </div>

        {/* Login / Register */}
        {(mode === "login" || mode === "register") && (
          <form className="mt-8 space-y-6" onSubmit={handleAuthSubmit} data-testid="auth-form">
            <div className="space-y-4">
              {mode === "register" && (
                <input
                  type="text"
                  required
                  data-testid="auth-fullname-input"
                  className={inputClass}
                  placeholder="Full Name"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                />
              )}
              <input
                type="email"
                required
                data-testid="auth-email-input"
                className={inputClass}
                placeholder="Email address"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <input
                type="password"
                required
                data-testid="auth-password-input"
                className={inputClass}
                placeholder="Password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
              {mode === "register" && (
                <p className="text-xs text-gray-500" data-testid="password-hint">
                  At least 8 characters, including a letter and a number.
                </p>
              )}
            </div>

            {error && <div className="text-red-600 text-sm text-center" data-testid="auth-error">{error}</div>}

            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-button"
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Sign Up"}
            </button>

            <div className="flex flex-col items-center space-y-2">
              {mode === "login" && (
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  className="text-blue-600 hover:text-blue-500 text-sm font-medium underline cursor-pointer"
                  onClick={() => switchMode("forgot")}
                >
                  Forgot your password?
                </button>
              )}
              <button
                type="button"
                data-testid="auth-toggle-button"
                className="text-blue-600 hover:text-blue-500 text-sm font-medium underline cursor-pointer"
                onClick={() => switchMode(mode === "login" ? "register" : "login")}
              >
                {mode === "login" ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
              </button>
            </div>
          </form>
        )}

        {/* Forgot password */}
        {mode === "forgot" && (
          <form className="mt-8 space-y-6" onSubmit={handleForgotSubmit} data-testid="forgot-form">
            <p className="text-sm text-gray-600 text-center">
              Enter your email and we'll generate a password reset link.
            </p>
            <input
              type="email"
              required
              data-testid="forgot-email-input"
              className={inputClass}
              placeholder="Email address"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
            {error && <div className="text-red-600 text-sm text-center" data-testid="auth-error">{error}</div>}
            <button
              type="submit"
              disabled={loading}
              data-testid="forgot-submit-button"
              className="w-full flex justify-center py-3 px-4 rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
            >
              {loading ? "Please wait..." : "Send reset link"}
            </button>
            <div className="text-center">
              <button
                type="button"
                data-testid="back-to-login-link"
                className="text-blue-600 hover:text-blue-500 text-sm font-medium underline cursor-pointer"
                onClick={() => switchMode("login")}
              >
                Back to sign in
              </button>
            </div>
          </form>
        )}

        {/* Reset password */}
        {mode === "reset" && (
          <form className="mt-8 space-y-6" onSubmit={handleResetSubmit} data-testid="reset-form">
            <input
              type="text"
              required
              data-testid="reset-token-input"
              className={inputClass}
              placeholder="Reset token"
              value={formData.resetToken}
              onChange={(e) => setFormData({ ...formData, resetToken: e.target.value })}
            />
            <input
              type="password"
              required
              data-testid="reset-password-input"
              className={inputClass}
              placeholder="New password"
              value={formData.newPassword}
              onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
            />
            <p className="text-xs text-gray-500" data-testid="reset-password-hint">
              At least 8 characters, including a letter and a number.
            </p>
            {error && <div className="text-red-600 text-sm text-center" data-testid="auth-error">{error}</div>}
            <button
              type="submit"
              disabled={loading}
              data-testid="reset-submit-button"
              className="w-full flex justify-center py-3 px-4 rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
            >
              {loading ? "Please wait..." : "Reset password"}
            </button>
            <div className="text-center">
              <button
                type="button"
                data-testid="back-to-login-link"
                className="text-blue-600 hover:text-blue-500 text-sm font-medium underline cursor-pointer"
                onClick={() => switchMode("login")}
              >
                Back to sign in
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AuthPage;
